import type { ChangeEvent, FC } from 'react';
import React, { useCallback, useEffect } from 'react';
import { ControlledModalDesktop as Modal } from '@alfa-bank/site-modal/controlled-desktop';
import { SearchInput } from '@alfa-bank/site-icon-input/search-input';
import { BlockHolder } from '@alfa-bank/site-block-holder';
import cn from 'classnames';

import {
    DEFAULT_ALIASES,
    DEFAULT_CITY_ICONS,
    DEFAULT_DESKTOP_TEXTS,
    DEFAULT_GEO_GROUPS,
    DEFAULT_TOP_CITIES,
} from '../config';
import type { TCitiesModalDesktop } from '../models';
import { CityHeader } from '../components/city-header';
import { List } from '../components/list';
import { noop, usePreventSubmit } from '../helpers';
import { useAnalytics } from '../features/analytics';
import { TagsTopCities } from '../components/tags-top-cities';
import { BoldText } from '../components/bold-text';
import { useCities } from '../features/hooks/use-cities';

export const CitiesModalDesktop: FC<TCitiesModalDesktop> = ({
    cities,
    geoGroups = DEFAULT_GEO_GROUPS,
    topCities = DEFAULT_TOP_CITIES,
    aliases = DEFAULT_ALIASES,
    cityIcons = DEFAULT_CITY_ICONS,
    onChange = noop,
    onClose = noop,
    geo,
    open = false,
    styles,
    dataTestId,
    analytics,
}) => {
    const { autoDetectedFiasId, bySearch, changeSearch, currentCity, searchValue, top } = useCities(
        {
            cityPath: geo?.cityPath,
            geoGroups: cities || geoGroups,
            topCities,
            aliases,
        }
    );

    useEffect(() => {
        if (!open) {
            changeSearch('');
        }
    }, [changeSearch, open]);

    const handleSubmit = usePreventSubmit();

    const { changeHandler, closeHandler, searchHandler } = useAnalytics(
        { open, onChange, onClose, changeSearch },
        analytics
    );

    const handleClear = useCallback(() => {
        searchHandler({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
    }, [searchHandler]);

    return (
        <Modal
            classNames={{
                wrapper: cn(styles.external, styles.modalWrapper),
                content: cn(styles.external, styles.modal),
            }}
            dataTestId={dataTestId}
            isOpen={open}
            onClose={closeHandler}
            size="m"
            styler={{ padding: 0 }}
        >
            {currentCity && (
                <CityHeader
                    city={currentCity}
                    // @ts-ignore
                    cityIcon={cityIcons[currentCity.fiasId]}
                    onClick={changeHandler}
                    searchMode={bySearch !== null}
                    styler={{ marginTop: 0, marginBottom: 16 }}
                />
            )}
            <SearchInput
                autoComplete="off"
                className={cn(styles.external, styles.search)}
                onChange={searchHandler}
                onClose={handleClear}
                onSubmit={handleSubmit}
                placeholder={DEFAULT_DESKTOP_TEXTS.searchPlaceholder}
                value={searchValue}
            />
            {bySearch === null && (
                <>
                    <BoldText styler={{ marginTop: 24, marginBottom: 24 }}>
                        {DEFAULT_DESKTOP_TEXTS.topCitiesHeader}
                    </BoldText>
                    <TagsTopCities
                        autoDetectedFiasId={autoDetectedFiasId}
                        onClick={city => changeHandler(city, true)}
                        topCities={top}
                    />
                </>
            )}
            {bySearch && bySearch.length > 0 && (
                <BlockHolder
                    alignX="left"
                    alignY="top"
                    className={cn(styles.external, styles.listHolder)}
                    styler={{ paddingLeft: 40 }}
                >
                    <List cities={bySearch} onClick={changeHandler} />
                </BlockHolder>
            )}
            {bySearch && bySearch.length === 0 && (
                <BoldText styler={{ marginTop: 24, marginBottom: 24 }}>
                    {DEFAULT_DESKTOP_TEXTS.emptyText}
                </BoldText>
            )}
        </Modal>
    );
};
